import moment from 'moment';

// 支払通知書(発行体 → 加盟店への支払のお知らせ)で使うデータ型と純粋ヘルパー。
// ビュー(レイアウト)は components/shared/PaymentNotice が担当し、ここは値の整形のみを扱う。
// すべての金額・派生値はサーバ算出済みの API 値をそのまま使い、フロントでの再計算はしない
// (合計/お支払金額は実振込額 actual_transfer_amount を正とする)。

// 請求額の相殺の内訳1件。相殺は相手先の請求データ(充当先)に対して行われ、
// 1つの支払が複数の請求に充当され得るため、通知書ではどの期間の請求へ充当したかを明示する。
export type PaymentNoticeOffset = {
  periodFrom: string; // 相手先請求データの対象期間(from)
  periodTo: string; // 相手先請求データの対象期間(to)
  amount: number; // 充当額
};

export type PaymentNoticeData = {
  id: string;
  // 電子マネー名(精算はマネー単位のため明示する)
  privateMoneyName: string;
  // 発行日(PDF 生成日)
  issueDate: string;
  // 発行体
  issuerName: string;
  issuerRegistrationNumber: string;
  // 期間・加盟店
  periodFrom: string;
  periodTo: string;
  merchantName: string;
  merchantRegistrationNumber: string;
  // サマリ
  paymentAmount: number; // お支払金額 = 実振込額
  scheduledPaymentDate: string; // お支払予定日(導出。未設定時は空)
  bankName: string;
  bankBranchName: string;
  bankAccountTypeLabel: string;
  bankAccount: string;
  bankAccountHolderName: string;
  // 明細
  transactionInvoiceAmount: number; // 取引請求額(チャージ請求額 + 決済手数料 + ポイント負担請求額)
  transactionPaymentAmount: number; // 取引支払額(決済支払額)
  offsetAmount: number; // 請求額の相殺(合計。予定込み。支払側 offset_amount と一致)
  // 相殺の内訳(充当先の請求データごと)。合算は offsetAmount と一致する。
  offsets: PaymentNoticeOffset[];
  systemFeeAmount: number; // 月額システム利用料(課税対象)
  fixedFeeAmount: number; // その他固定費(課税対象)
  consumptionTaxAmount: number; // 消費税額
};

const ACCOUNT_TYPE_LABELS: Record<string, string> = {
  saving: '普通',
  current: '当座',
  other: 'その他',
};

export const bankAccountTypeLabel = (type?: string | null): string =>
  (type && ACCOUNT_TYPE_LABELS[type]) || '';

// 法人の適格請求書発行事業者登録番号は "T" + 法人番号(13桁)。法人番号が無ければ空。
export const merchantRegistrationNumber = (corporateNumber?: string | null): string =>
  corporateNumber ? `T${corporateNumber}` : '';

// お支払予定日: 締め日(closingDate)の翌日以降で最も早い精算日を導出する。
// settlementDates は monthly_settlement_dates(0=月末)。設定が無ければ null を返す(=空表示)。
export const computeScheduledPaymentDate = (
  closingDate: string | undefined,
  settlementDates: number[] | null | undefined
): moment.Moment | null => {
  if (!closingDate || !settlementDates || settlementDates.length === 0) return null;
  const closing = moment(closingDate);
  if (!closing.isValid()) return null;

  // 締め日の当月から最大13ヶ月先までを走査し、締め日より後の最初の精算日を採用する。
  // (締め日自体が精算日の場合があるため「翌日以降」で判定する)
  for (let monthOffset = 0; monthOffset <= 13; monthOffset += 1) {
    const base = closing.clone().add(monthOffset, 'months');
    const candidates = settlementDates
      .map((day) => {
        // 0 = 月末。月によって末日が異なるため endOf('month') で吸収する。
        if (day === 0) return base.clone().endOf('month').startOf('day');
        const lastDay = base.clone().endOf('month').date();
        // 31 指定などで当月に存在しない日は末日へ丸める。
        return base.clone().date(Math.min(day, lastDay)).startOf('day');
      })
      .filter((d) => d.isAfter(closing, 'day'))
      .sort((a, b) => a.valueOf() - b.valueOf());
    if (candidates.length > 0) return candidates[0];
  }
  return null;
};

// 金額表記(整数丸め + 桁区切り + 円)。ビュー側でそのまま表示する。
export const yen = (n: number): string => `${Math.round(n).toLocaleString()}円`;
